import { noughts, type NoughtsRules } from '@cardengine/shared';
import { useStore } from '../store.js';

interface NoughtsRulesEditorProps {
  rules: NoughtsRules;
  editable: boolean;
}

const BOARD_OPTIONS: { value: number; label: string; hint: string }[] = [
  { value: 3, label: 'Three by three', hint: 'Nine squares. The game as everyone knows it.' },
  { value: 4, label: 'Four by four', hint: 'Sixteen squares — far more room, and far more to watch.' },
  { value: 5, label: 'Five by five', hint: 'Twenty-five squares. Long games, and threats from everywhere.' },
];

/**
 * Noughts and crosses' settings.
 *
 * Deliberately built in the same shape as the three card editors, down to the
 * class names, so a lobby looks like one page whatever is being played.
 *
 * The win length is the one control that has to answer to another: three in a
 * row on a five-wide board is a very different game from five, and asking for
 * more in a row than the board is wide is a game nobody can win. The engine
 * clamps it regardless — this only offers what makes sense, so the clamp never
 * has to surprise anyone.
 */
export function NoughtsRulesEditor({ rules, editable }: NoughtsRulesEditorProps) {
  const updateRules = useStore((s) => s.updateRules);

  const set = <K extends keyof NoughtsRules>(key: K, value: NoughtsRules[K]) => {
    if (editable) updateRules({ [key]: value } as Partial<NoughtsRules>);
  };

  const activePreset = noughts.RULE_PRESETS.find(
    (preset) => JSON.stringify(preset.rules) === JSON.stringify(rules),
  );

  const winOptions = Array.from(
    { length: rules.boardSize - noughts.MIN_WIN_LENGTH + 1 },
    (_unused, index) => noughts.MIN_WIN_LENGTH + index,
  );

  return (
    <section className="rules" aria-label="Noughts and crosses settings">
      <header className="rules__header">
        <h2>Settings</h2>
      </header>

      <div className="presets">
        <span className="presets__label">Preset</span>
        <div className="presets__row">
          {noughts.RULE_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={`presets__button ${activePreset?.id === preset.id ? 'is-active' : ''}`}
              disabled={!editable}
              onClick={() => editable && updateRules(preset.rules)}
              title={preset.description}
            >
              {preset.name}
            </button>
          ))}
        </div>
        <p className="presets__description">
          {activePreset ? activePreset.description : 'Custom settings.'}
        </p>
      </div>

      <div className="rules__ranks">
        <label className="rule rule--choice">
          <span className="rule__label">Grid</span>
          <select
            value={rules.boardSize}
            disabled={!editable}
            onChange={(event) => {
              const boardSize = Number(event.target.value);
              // Sent together: a shrinking board can strand a win length that
              // no longer fits, and two round trips would show a moment of
              // rules the server was always going to reject.
              if (editable) {
                updateRules({ boardSize, winLength: Math.min(rules.winLength, boardSize) });
              }
            }}
          >
            {BOARD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="rule__hint">
            {BOARD_OPTIONS.find((option) => option.value === rules.boardSize)?.hint}
          </span>
        </label>

        <label className="rule rule--choice">
          <span className="rule__label">In a row to win</span>
          <select
            value={rules.winLength}
            disabled={!editable || winOptions.length === 1}
            onChange={(event) => set('winLength', Number(event.target.value))}
          >
            {winOptions.map((length) => (
              <option key={length} value={length}>
                {length}
              </option>
            ))}
          </select>
          <span className="rule__hint">
            {rules.winLength < rules.boardSize
              ? 'Shorter than the grid is wide, so lines can appear anywhere — much harder to defend.'
              : 'A line has to span the whole grid.'}
          </span>
        </label>
      </div>

      <div className="rules__toggles">
        <label className={`rule ${!editable ? 'rule--locked' : ''}`}>
          <input
            type="checkbox"
            checked={rules.misere}
            disabled={!editable}
            onChange={(event) => set('misere', event.target.checked)}
          />
          <span className="rule__body">
            <span className="rule__label">Making a line loses</span>
            <span className="rule__hint">
              The playground version, upside down. The board fills, the safe squares run out, and
              somebody has to go first into the trap.
            </span>
          </span>
        </label>
      </div>
    </section>
  );
}
